import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Navbar from './Navbar';
import VaultoLogo from './VaultoLogo';
import Footer from './Footer';
import { authAPI } from '../services/api';

const ForgotPassword = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    
    try {
      await authAPI.requestPasswordReset(data);
      setIsSubmitted(true);
    } catch (err) {
      setError('An error occurred. Please try again later.');
      console.error('Password reset request error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <Navbar />
      <div className="relative flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Glassmorphism container */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="text-center">
              {/* Logo */}
              <div className="mx-auto mb-6">
                <VaultoLogo className="h-16 w-auto mx-auto" showText={true} />
              </div>
              
              <h2 className="text-3xl font-extrabold text-white mb-2">
                Forgot Password?
              </h2>
              <p className="text-gray-300 text-sm">
                {isSubmitted 
                  ? "Check your email for reset instructions"
                  : "No worries! Enter your email and we'll send you reset instructions"
                }
              </p>
            </div>
            
            {!isSubmitted ? (
              <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      className={`appearance-none rounded-xl relative block w-full px-4 py-3 border-0 bg-white/10 backdrop-blur-sm ${
                        errors.email ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                      } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                      placeholder="Enter your email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-400">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div className="bg-red-500/20 backdrop-blur-sm border border-red-400/50 text-red-200 px-4 py-3 rounded-xl flex items-start space-x-3">
                    <svg className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                  >
                    {loading ? (
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-white/10">
                  <p className="text-sm text-gray-300">
                    Remember your password?{' '}
                    <Link
                      to="/login"
                      className="font-semibold text-pink-400 hover:text-pink-300 transition-colors"
                    >
                      Back to login
                    </Link>
                  </p>
                </div>
              </form>
            ) : (
              <div className="mt-8 space-y-6">
                {/* Success state */}
                <div className="bg-green-500/20 backdrop-blur-sm border border-green-400/50 text-green-200 px-4 py-4 rounded-xl flex items-start space-x-3">
                  <svg className="h-6 w-6 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-semibold">Email sent!</p>
                    <p className="text-sm mt-1">If an account exists with this email, you will receive password reset instructions shortly.</p>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 space-y-3">
                  <p className="text-gray-300 text-sm font-medium">What's next?</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-start">
                      <span className="text-pink-400 mr-2">1.</span>
                      <span>Check your email inbox (and spam folder)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-pink-400 mr-2">2.</span>
                      <span>Click the reset link in the email</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-pink-400 mr-2">3.</span>
                      <span>Create a new password</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-pink-400 mr-2">4.</span>
                      <span>Log in with your new password</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center pt-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center py-3 px-6 border border-white/20 text-base font-semibold rounded-xl text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200"
                  >
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to login
                  </Link>
                </div>

                <div className="text-center pt-4 border-t border-white/10">
                  <p className="text-sm text-gray-400 mb-3">Didn't receive the email?</p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-sm font-semibold text-pink-400 hover:text-pink-300 transition-colors"
                  >
                    Try another email address
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer variant="dark" />
    </div>
  );
};

export default ForgotPassword;

